import { fetchPost } from "app/utils/request";
import i18n from "app/i18n";
import { FamilyMember, FamilyMemberFormData } from "./types";

const FAMILY_ENDPOINT = "family_memberships";
const FAMILY_API_OPTS = { version: 2 };

/**
 * Creates a managed family member and returns the created record (CN-1323).
 * Shared by the Family Management page and the on-the-spot add during the
 * booking participant picker (CFTB-3277).
 */
export function createFamilyMember(
  data: FamilyMemberFormData,
): Promise<FamilyMember> {
  const attributes: Record<string, unknown> = {
    first_name: data.firstName,
    last_name: data.lastName,
    date_of_birth: data.dateOfBirth,
    consent: data.proxyConsent,
  };
  if (data.avatarId) {
    attributes.avatar_id = data.avatarId;
  }

  return fetchPost<FamilyMember>(
    FAMILY_ENDPOINT,
    { family_membership: attributes },
    FAMILY_API_OPTS,
  );
}

/**
 * Builds a human-readable message from a rejected family-membership request.
 * The API returns 422 with `{ errors: { field: [messages] } }`. The request
 * layer's flattened `errors` array is lossy (an unmapped message collapses to a
 * single character), but it preserves the raw body under `originalErrors` — so
 * we read that. Returns null when nothing useful can be extracted, letting
 * callers fall back to a generic message. Example:
 * `{ date_of_birth: ["must be under 13"] }` -> "Date of birth must be under 13".
 */
export function familyMembershipErrorMessage(err: unknown): string | null {
  const original = (err as { originalErrors?: unknown } | null | undefined)
    ?.originalErrors;
  if (
    original == null ||
    typeof original !== "object" ||
    Array.isArray(original)
  ) {
    return null;
  }
  const parts = Object.entries(original as Record<string, unknown>)
    .map(([field, messages]) => {
      const list = Array.isArray(messages) ? messages : [messages];
      const text = list.filter(Boolean).join(", ");
      if (!text) {
        return null;
      }
      return friendlyFieldMessage(field) ?? `${humanizeField(field)} ${text}`;
    })
    .filter((part): part is string => part != null);
  return parts.length > 0 ? parts.join(". ") : null;
}

function humanizeField(field: string): string {
  const words = field.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Friendly, translated copy that replaces the raw Rails text for known fields.
// The family_membership model's only date_of_birth validation is the under-13
// COPPA limit, so any date_of_birth error means the age rule was violated.
// Returns null for other fields (caller falls back to "<Field> <server message>").
function friendlyFieldMessage(field: string): string | null {
  if (field === "date_of_birth") {
    return i18n.t(
      "Family members can only be created for children under 13 years old. New users who are over 13 years old must create their own account.",
    );
  }
  return null;
}

/**
 * Returns the age in whole years for an ISO date string (yyyy-mm-dd).
 * Returns null when the input is empty or unparseable.
 */
export function getAge(dateOfBirth: string): number | null {
  if (!dateOfBirth) {
    return null;
  }

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  return age;
}
